// @flow
import { BASE_MEDIA_PATH } from 'config/env';
import { NavigationUtil } from 'utils/NavigationUtil';
import { StringUtil } from 'utils/StringUtil';
import type {
  Contact,
  GalleryImageType,
  LocationType,
  MediaPath,
  SiteSettings,
  Vertical,
  WebsiteSettingsType,
} from 'types/common';
import { remove, sortBy } from 'lodash';

const DEFAULT_DEVICE_MAP = {
  web: 'Web',
  mobile: 'Mobile',
  thumbnail: 'Thumbnail',
};

const extractMediaPath = (response: any): MediaPath => {
  const mediaPath = response.media_path || BASE_MEDIA_PATH;
  const deviceMap = response.device || DEFAULT_DEVICE_MAP;

  return {
    mobile: `${mediaPath}${deviceMap.mobile}`,
    thumbnail: `${mediaPath}${deviceMap.thumbnail}`,
    web: `${mediaPath}${deviceMap.web}`,
  };
};

const mapGalleryImage = (data: any, mediaPath: MediaPath): GalleryImageType => {
  const mediumMobileImageUrl = `${mediaPath.mobile}/${data.medium_mobile_picture}`;
  const mediumMobileThumbnailImageUrl = `${mediaPath.thumbnail}/${data.medium_picture}`;
  const mediumImageUrl = `${mediaPath.web}/${data.medium_picture}`;
  const largeImageUrl = data.large_picture
    ? `${mediaPath.web}/${data.large_picture}`
    : mediumImageUrl;
  const thumbnailMediumImageUrl = `${mediaPath.thumbnail}/${data.medium_picture}`;
  const thumbnailLargeImageUrl = data.large_picture
    ? `${mediaPath.thumbnail}/${data.large_picture}`
    : thumbnailMediumImageUrl;
  const outlet_id = data.foreign_key ? data.foreign_key : '';
  return {
    id: data.id,
    outlet_id,
    caption: data.gallery_name,
    mediumImageUrl,
    largeImageUrl,
    mediumMobileImageUrl,
    mediumMobileThumbnailImageUrl,
    thumbnailMediumImageUrl,
    thumbnailLargeImageUrl,
  };
};

const mapGalleryImages = (
  images: Array<any>,
  mediaPath: MediaPath
): Array<GalleryImageType> => {
  if (!images || images.length === 0) {
    return [];
  }

  return images.map(image => mapGalleryImage(image, mediaPath));
};

const mapVertical = (data: any, mediaPath: MediaPath): Vertical => {
  const imageUrl = `${mediaPath.web}/${data.vertical_image}`;
  const mobileImageUrl = `${mediaPath.mobile}/${data.mobile_vertical_image}`;
  const iconUrl = `${mediaPath.web}/${data.vertical_icon}`;
  const thumbnailUrl = `${mediaPath.thumbnail}/${data.vertical_image}`;
  const thumbnailMobileUrl = `${mediaPath.thumbnail}/${data.vertical_image_mobile}`;
  const sanitizedName = StringUtil.sliceHtml(data.vertical_name);
  const route = NavigationUtil.getRouteForVertical(sanitizedName);
  const metaTagSEO = {
    metaTitle: data.meta_title ? data.meta_title : '',
    metaDescription: data.meta_description ? data.meta_description : '',
    metaKeywords: data.meta_keyword ? data.meta_keyword : '',
  };

  return {
    id: data.id,
    route,
    name: data.vertical_name,
    sanitizedName,
    description: data.vertical_description,
    imageUrl,
    mobileImageUrl,
    iconUrl,
    thumbnailUrl,
    thumbnailMobileUrl,
    metaTagSEO,
  };
};

const mapVerticals = (
  data: Array<any>,
  mediaPath: MediaPath
): Array<Vertical> => {
  if (!data || data.length === 0) {
    return [];
  }

  const returned_data = data.map(item => mapVertical(item, mediaPath));
  const filtered_data = remove(returned_data, function(e) {
    return e.id !== 10;
  });

  // var sortedItems = sortBy(filtered_data, function(item) {
  //   return item.id === 3 ? 0 : item.id === 8 ? 1 : item.id === 9 ? 2 : 3;
  // });
  return filtered_data;
};

const mapContact = (data: any, mediaPath: MediaPath): Contact => {
  const vertical = data.vertical ? mapVertical(data.vertical, mediaPath) : null;
  return {
    id: data.id,
    type: data.type,
    title: data.title,
    description: data.description,
    address: data.address,
    email: data.email,
    phone: data.phone,
    verticalId: data.vertical_id,
    vertical,
    projectId: data.project_id,
    productId: data.product_id,
    campaignCode: data.campaign_code,
    campaignId: data.campaign_id,
  };
};

const mapContacts = (
  data: Array<any>,
  mediaPath: MediaPath
): Array<Contact> => {
  if (!data || data.length === 0) {
    return [];
  }
  return data.map(item => mapContact(item, mediaPath));
};

const mapSiteSettings = (data: any, mediaPath: MediaPath): SiteSettings => {
  return {
    settings: mapSettings(data.site_settings, mediaPath),
    social: data.social_settings,
  };
};

const mapSettings = (data: any, mediaPath: MediaPath): WebsiteSettingsType => {
  return {
    adminEmail: data.admin_email,
    author: data.author,
    companyLogoDarkUrl: data.company_logo_dark,
    footerBannerUrl: `${mediaPath.web}/${data.footer_banner}`,
    footerContent: data.footer_content,
    menuContent: data.menu_content,
    mobileBlackLogoUrl: data.mobile_black_logo,
    mobileFooterBannerUrl: `${mediaPath.mobile}/${data.mobile_footer_banner}`,
    thumbNailFooterBannerUrl: `${mediaPath.thumbnail}/${data.footer_banner}`,
    mobileWhiteLogoUrl: data.mobile_white_logo,
    siteName: data.site_name,
    squirrelBlackLogoUrl: data.squirrel_black_logo,
    squirrelWhiteLogoUrl: data.squirrel_white_logo,
    website: data.website,
    websiteBlackLogoUrl: data.website_black_logo,
    websiteWhiteLogoUrl: data.website_white_logo,
  };
};

const mapUserLocation = (data: any): LocationType => {
  return {
    country: data.country,
    countryCode: data.countryCode,
    region: data.region,
    regionName: data.regionName,
    city: data.city,
    zip: data.zip,
    latitude: data.lat,
    longitude: data.lon,
    timezone: data.timezone,
  };
};
export const CommonMapper = {
  extractMediaPath,
  mapGalleryImages,
  mapVertical,
  mapVerticals,
  mapContacts,
  mapSiteSettings,
  mapUserLocation,
};
